import React, { useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { Tooltip } from '@material-ui/core';
import ReactSVG from 'react-svg';
import * as actionTypes from '~/store/actions/actionTypes';
import { RootState } from '~/store/config';
import {
  ContactIntegratedList,
  ContactIntegrated,
  ActiveIntegrations,
} from '~/interfaces/integrations';
import * as lib from '~/dependencies';
import api from '~/services';
import { log } from '~/helpers';
import { IntegrationIds } from '~/helpers/contactsIntegration';
import {
  SyncAllCallsIcon,
  SyncSelectedCallsIcon,
  SyncNoCalls,
} from '~/assets/svgIcons';
import { LogContactIconButton } from './styles';

export const setCallSyncWithCRMStateFalseForOthers = (
  arrayContacts: ContactIntegratedList,
  externalContactId: string
) => {
  return arrayContacts.map(contactObj => ({
    ...contactObj,
    callSyncWithCRM:
      contactObj.externalId !== externalContactId
        ? false
        : contactObj.callSyncWithCRM,
  }));
};

export const setCallSyncWithCRMStateForSpecificContact = (
  arrayContacts: ContactIntegratedList,
  externalContactId: string,
  nextBtnVisibilityState: boolean
) => {
  return arrayContacts.map(contactObj => ({
    ...contactObj,
    callSyncWithCRM:
      contactObj.externalId === externalContactId
        ? nextBtnVisibilityState
        : contactObj.callSyncWithCRM,
  }));
};

type Props = {
  callId: string;
  originalCallId: string | null;
  integrationId: string | number;
  /**
   * The contact ID the user has click on it
   */
  externalContactId: string;
  integrationAccountId: number | null;
};

export default function SyncCallButton(props: Props) {
  const dispatch = useDispatch();

  const {
    callId,
    originalCallId,
    integrationId,
    externalContactId,
    integrationAccountId,
  } = props;

  const allCrmContacts: ContactIntegratedList = useSelector(
    (state: RootStateOrAny) => state.integrations.contacts
  );

  const curIntegrationInfo: ActiveIntegrations = useSelector(
    (state: RootStateOrAny) =>
      state.integrations?.accountIntegrations?.find(
        (intConfig: ActiveIntegrations) => intConfig.id === integrationId
      )
  );

  // Get all contacts from reducer and filter for current callId and integrationId
  const curIntCrmContacts: ContactIntegratedList = useSelector(
    (state: RootState) =>
      // @ts-ignore
      state.integrations.contacts?.filter(
        (cc: ContactIntegrated) =>
          cc.callId === callId && cc.integrationId === integrationId
      )
  );

  const isCompatibleModeEnabled =
    curIntegrationInfo?.id &&
    (Number(curIntegrationInfo.id) === IntegrationIds.SALESFORCE ||
      String(curIntegrationInfo.id).startsWith(
        IntegrationIds.GO_HIGH_LEVEL_ID.toString()
      )) &&
    curIntegrationInfo?.compatibleModeEnabled;

  /**
   * Return true when all contacts are marked to be synced.
   */

  const isSyncForAllContacts = curIntCrmContacts?.every(
    cc => cc.callSyncWithCRM === true
  );

  // A integration can have many contacts, so pick the current visible contact on screen
  const curContactInfo = curIntCrmContacts?.find(
    cc => cc.externalId === externalContactId
  );

  const handleCallSyncLogic = useCallback(async () => {
    try {
      log.info('NUACOM-LOG: Before sync call to contact logging.', {
        callId,
        integrationId,
        externalContactId,
        originalCallId,
        curContactInfo,
        isCompatibleModeEnabled,
        isSyncForAllContacts,
        integrationAccountId,
      });

      if (!callId || !externalContactId || !curContactInfo?.callId) return;

      const defaultPayloadProperties = {
        call_id: originalCallId || callId,
        integration_id: String(integrationId).startsWith(
          IntegrationIds.GO_HIGH_LEVEL_ID.toString()
        )
          ? Number(String(integrationId).split('.')[0])
          : integrationId,
        integration_account_id: integrationAccountId,
      };

      if (isCompatibleModeEnabled) {
        if (isSyncForAllContacts) {
          await api.integrations.post.unSyncCallForCRMContacts({
            ...defaultPayloadProperties,
            contact_ids: curIntCrmContacts.map(cc => cc.externalId),
          });

          dispatch({
            type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
            payload: allCrmContacts.map(cc => ({
              ...cc,
              callSyncWithCRM: false,
            })),
          });
          lib.ToastMessage(
            'warn',
            'This call won’t be synced to all contacts.'
          );
        } else {
          await api.integrations.post.syncCallWithCRMContacts({
            ...defaultPayloadProperties,
            contact_ids: curIntCrmContacts.map(cc => cc.externalId),
          });

          dispatch({
            type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
            payload: allCrmContacts.map(cc => ({
              ...cc,
              callSyncWithCRM: true,
            })),
          });

          lib.ToastMessage('info', 'This call will be synced to all contacts.');
        }
      } else if (isSyncForAllContacts) {
        if (curIntCrmContacts?.length > 1) {
          // THEN user click on blue icon -> which means all contacts are marked to sync ("Sync All")
          // AND there is more contacts
          // THEN mark all other contacts as unsync AND change to green icon -> which means sync selected contact only
          await api.integrations.post.unSyncCallForCRMContacts({
            ...defaultPayloadProperties,
            contact_ids: curIntCrmContacts
              ?.filter(cc => cc.externalId !== externalContactId)
              .map(cc => cc.externalId),
          });
          dispatch({
            type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
            payload: setCallSyncWithCRMStateFalseForOthers(
              allCrmContacts,
              externalContactId
            ),
          });
          lib.ToastMessage(
            'info',
            `This call will be synced to the selected contact.`
          );
        } else {
          // THEN user click on blue icon -> which means all contacts are marked to sync ("Sync All")
          // AND there is a single contact
          // THEN mark the contact as unsync (red icon)
          await api.integrations.post.unSyncCallForCRMContacts({
            ...defaultPayloadProperties,
            contact_ids: [curContactInfo?.externalId],
          });
          dispatch({
            type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
            payload: setCallSyncWithCRMStateForSpecificContact(
              allCrmContacts,
              externalContactId,
              false
            ),
          });
          lib.ToastMessage(
            'warn',
            `This call won’t be synced to the selected contact.`
          );
        }
      } else if (curContactInfo.callSyncWithCRM) {
        await api.integrations.post.unSyncCallForCRMContacts({
          ...defaultPayloadProperties,
          contact_ids: [curContactInfo.externalId],
        });

        dispatch({
          type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
          payload: setCallSyncWithCRMStateForSpecificContact(
            allCrmContacts,
            externalContactId,
            false
          ),
        });

        lib.ToastMessage(
          'warn',
          `This call won’t be synced to the selected contact. ${curContactInfo.firstName}`
        );
      } else if (!curContactInfo.callSyncWithCRM) {
        await api.integrations.post.syncCallWithCRMContacts({
          ...defaultPayloadProperties,
          contact_ids: [curContactInfo.externalId],
        });

        dispatch({
          type: actionTypes.UPDATE_CONTACTS_CALL_SYNC_WITH_CRM,
          payload: setCallSyncWithCRMStateForSpecificContact(
            allCrmContacts,
            externalContactId,
            true
          ),
        });
      }
    } catch (err) {
      log.error('Error on sync call with contacts.', err);
      lib.ToastMessage('error', 'Error on sync call with contacts.');
    }
  }, [
    isCompatibleModeEnabled,
    isSyncForAllContacts,
    callId,
    originalCallId,
    integrationId,
    integrationAccountId,
    externalContactId,
    curContactInfo,
    curIntCrmContacts,
    allCrmContacts,
    dispatch,
  ]);

  function renderSyncButtons() {
    if (isCompatibleModeEnabled) {
      if (isSyncForAllContacts) {
        return (
          <Tooltip
            data-test-id="sync-call-with-all-contacts-button"
            title="This call will be synced to all contacts."
            placement="bottom-start">
            <LogContactIconButton variant="text" onClick={handleCallSyncLogic}>
              <ReactSVG src={SyncAllCallsIcon} />
            </LogContactIconButton>
          </Tooltip>
        );
      }

      return (
        <Tooltip
          data-test-id="unsync-call-with-selected-contact-button"
          title="This call won't be synced to the selected contact."
          placement="bottom-start">
          <LogContactIconButton variant="text" onClick={handleCallSyncLogic}>
            <ReactSVG src={SyncNoCalls} />
          </LogContactIconButton>
        </Tooltip>
      );
    }

    if (isSyncForAllContacts) {
      return (
        <Tooltip
          data-test-id="sync-call-with-all-contacts-button"
          title="This call will be synced to all contacts."
          placement="bottom-start">
          <LogContactIconButton variant="text" onClick={handleCallSyncLogic}>
            <ReactSVG src={SyncAllCallsIcon} />
          </LogContactIconButton>
        </Tooltip>
      );
    }

    if (curContactInfo?.callSyncWithCRM === true) {
      return (
        <Tooltip
          data-test-id="sync-call-with-selected-contact-button"
          title="This call will be synced to the selected contact."
          placement="bottom-start">
          <LogContactIconButton variant="text" onClick={handleCallSyncLogic}>
            <ReactSVG src={SyncSelectedCallsIcon} />
          </LogContactIconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        data-test-id="unsync-call-with-selected-contact-button"
        title="This call won't be synced to the selected contact."
        placement="bottom-start">
        <LogContactIconButton variant="text" onClick={handleCallSyncLogic}>
          <ReactSVG src={SyncNoCalls} />
        </LogContactIconButton>
      </Tooltip>
    );
  }

  return renderSyncButtons();
}
